import React, { useEffect, useState } from "react"
import { resolveDefaultCountry, COUNTRY_NAMES } from "./signupCountries"
import {
  EMAIL_PATTERN,
  RECAPTCHA_VALIDATION_ERROR,
} from "./signupConfig"
import { loadRecaptchaScript, executeRecaptcha } from "./recaptcha"
import { submitProviderInterest } from "./submitProviderInterest"
import { signInWithGoogle, observeGoogleUser } from "./firebaseAuth"
import { getGoogleSignInErrorMessage } from "./googleSignInError"

const EMPTY_VALUES = {
  name: "",
  email: "",
  businessName: "",
  companyRegistrationNumber: "",
  country: "",
  phoneNumber: "",
}

function validate(values) {
  const errors = {}
  if (!values.name.trim()) errors.name = "Name is required"
  if (!values.email.trim()) errors.email = "Email is required"
  else if (!EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = "Enter a valid email address"
  }
  if (!values.businessName.trim()) errors.businessName = "Business name is required"
  if (!values.country.trim()) errors.country = "Country is required"
  if (!values.phoneNumber.trim()) errors.phoneNumber = "Phone number is required"
  return errors
}

export default function ProviderInterestForm({
  market = "gb",
  pageRoute = "/",
  onSubmitted,
}) {
  const defaultCountry = resolveDefaultCountry(market)
  const [values, setValues] = useState({ ...EMPTY_VALUES, country: defaultCountry })
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [loading, setLoading] = useState(false)
  const [googleUser, setGoogleUser] = useState(null)
  const [googleSignInError, setGoogleSignInError] = useState("")
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false)
  const [googleSubmissionPending, setGoogleSubmissionPending] = useState(false)

  useEffect(() => {
    setValues((prev) => ({ ...prev, country: resolveDefaultCountry(market) }))
  }, [market])

  useEffect(() => {
    loadRecaptchaScript().catch(() => {})
  }, [])

  useEffect(() => {
    let active = true
    let unsubscribe

    observeGoogleUser((user) => {
      if (!active || !user) return
      setGoogleUser(user)
      setValues((previous) => ({
        ...previous,
        name: user.displayName || previous.name,
        email: user.email || previous.email,
      }))
    })
      .then((stopObserving) => {
        if (active) unsubscribe = stopObserving
        else stopObserving()
      })
      .catch(() => {})

    return () => {
      active = false
      if (unsubscribe) unsubscribe()
    }
  }, [])

  const onChange = (event) => {
    const { name, value } = event.target
    setValues((prev) => ({ ...prev, [name]: value }))
  }

  const submitValues = async (submissionValues, allowPartial = false) => {
    const nextErrors = allowPartial ? {} : validate(submissionValues)
    setErrors(nextErrors)
    setSubmitError("")
    if (Object.keys(nextErrors).length) return

    setLoading(true)
    try {
      let captcha
      try {
        await loadRecaptchaScript()
        captcha = await executeRecaptcha("provider_interest")
      } catch (_error) {
        setSubmitError(RECAPTCHA_VALIDATION_ERROR)
        return
      }
      if (!captcha) {
        setSubmitError(RECAPTCHA_VALIDATION_ERROR)
        return
      }

      await submitProviderInterest({
        captcha,
        name: submissionValues.name.trim(),
        email: submissionValues.email.trim(),
        businessName: submissionValues.businessName.trim() || null,
        companyRegistrationNumber:
          submissionValues.companyRegistrationNumber.trim() || null,
        country: submissionValues.country.trim(),
        phoneNumber: submissionValues.phoneNumber.trim() || null,
        pageRoute,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
      })

      setSubmitted(true)
      if (typeof onSubmitted === "function") onSubmitted()
    } catch (_error) {
      setSubmitError("Something went wrong. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const onGoogleSignIn = async () => {
    setGoogleSignInLoading(true)
    setGoogleSignInError("")

    try {
      const user = googleUser || (await signInWithGoogle())
      setGoogleUser(user)
      const googleValues = {
        ...values,
        name: user.displayName || values.name,
        email: user.email || values.email,
      }
      setValues(googleValues)
      setGoogleSubmissionPending(true)
      await submitValues(googleValues, true)
    } catch (error) {
      setGoogleSignInError(getGoogleSignInErrorMessage(error))
    } finally {
      setGoogleSignInLoading(false)
    }
  }

  const onSubmit = async (event) => {
    event.preventDefault()
    await submitValues(values)
  }

  if (submitted) {
    return (
      <div className="signup-form signup-form--thanks">
        <h2 className="signup-form__title">Thank you</h2>
        <p className="signup-form__message">
          Check your email to get started.
        </p>
      </div>
    )
  }

  if (googleSubmissionPending && loading) {
    return (
      <div className="signup-form signup-form--thanks" role="status">
        <p className="signup-form__message">Submitting…</p>
      </div>
    )
  }

  if (googleSubmissionPending && submitError) {
    return (
      <div className="signup-form signup-form--thanks">
        <p className="signup-form__error signup-form__error--block" role="alert">
          {submitError}
        </p>
        <button
          type="button"
          className="signup-form__submit"
          onClick={() => submitValues(values, true)}
          disabled={loading}
        >
          {loading ? "Submitting…" : "Submit"}
        </button>
      </div>
    )
  }

  return (
    <form className="signup-form" onSubmit={onSubmit} noValidate>
      <h2 className="signup-form__title">Sign Up</h2>

      <button
        type="button"
        className="signup-form__google"
        onClick={onGoogleSignIn}
        disabled={googleSignInLoading}
        aria-busy={googleSignInLoading}
      >
        <svg className="signup-form__google-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        <span className="signup-form__google-label">
          {googleSignInLoading ? "Connecting to Google…" : "Sign up with Google"}
        </span>
      </button>

      {googleSignInError ? (
        <p className="signup-form__error signup-form__error--block" role="alert">
          {googleSignInError}
        </p>
      ) : null}

      {googleUser ? (
        <p className="signup-form__google-success" role="status">
          Signed in with Google as <strong>{googleUser.email}</strong>. Your name and email are ready below.
        </p>
      ) : null}

      <div className="signup-form__divider" aria-hidden="true"><span>or</span></div>

      <label className="signup-form__field" htmlFor="signup-name">
        <span className={errors.name ? "is-error" : undefined}>Name</span>
        <input
          id="signup-name"
          name="name"
          type="text"
          autoComplete="name"
          value={values.name}
          onChange={onChange}
          className={errors.name ? "is-error" : undefined}
        />
        {errors.name ? <em className="signup-form__error">{errors.name}</em> : null}
      </label>

      <label className="signup-form__field" htmlFor="signup-email">
        <span className={errors.email ? "is-error" : undefined}>Email</span>
        <input
          id="signup-email"
          name="email"
          type="email"
          autoComplete="email"
          value={values.email}
          onChange={onChange}
          className={errors.email ? "is-error" : undefined}
        />
        {errors.email ? <em className="signup-form__error">{errors.email}</em> : null}
      </label>

      <label className="signup-form__field" htmlFor="signup-businessName">
        <span className={errors.businessName ? "is-error" : undefined}>Business name</span>
        <input
          id="signup-businessName"
          name="businessName"
          type="text"
          autoComplete="organization"
          value={values.businessName}
          onChange={onChange}
          className={errors.businessName ? "is-error" : undefined}
        />
        {errors.businessName ? (
          <em className="signup-form__error">{errors.businessName}</em>
        ) : null}
      </label>

      <label className="signup-form__field" htmlFor="signup-companyRegistrationNumber">
        <span>Company registration no (optional)</span>
        <input
          id="signup-companyRegistrationNumber"
          name="companyRegistrationNumber"
          type="text"
          value={values.companyRegistrationNumber}
          onChange={onChange}
        />
      </label>

      <label className="signup-form__field" htmlFor="signup-country">
        <span className={errors.country ? "is-error" : undefined}>Country</span>
        <select
          id="signup-country"
          name="country"
          value={values.country || defaultCountry}
          onChange={onChange}
          className={errors.country ? "is-error" : undefined}
        >
          {COUNTRY_NAMES.map((countryName) => (
            <option key={countryName} value={countryName}>
              {countryName}
            </option>
          ))}
        </select>
        {errors.country ? <em className="signup-form__error">{errors.country}</em> : null}
      </label>

      <label className="signup-form__field" htmlFor="signup-phoneNumber">
        <span className={errors.phoneNumber ? "is-error" : undefined}>Phone number</span>
        <input
          id="signup-phoneNumber"
          name="phoneNumber"
          type="tel"
          autoComplete="tel"
          value={values.phoneNumber}
          onChange={onChange}
          className={errors.phoneNumber ? "is-error" : undefined}
        />
        {errors.phoneNumber ? (
          <em className="signup-form__error">{errors.phoneNumber}</em>
        ) : null}
      </label>

      {submitError ? <p className="signup-form__error signup-form__error--block">{submitError}</p> : null}

      <button type="submit" className="signup-form__submit" disabled={loading}>
        {loading ? "Submitting…" : "Submit"}
      </button>

      <p className="signup-form__recaptcha-disclosure">
        This site is protected by reCAPTCHA.{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
          Privacy Policy
        </a>{" "}
        and{" "}
        <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
          Terms of Service
        </a>{" "}
        apply.
      </p>
    </form>
  )
}
